import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import {
  BirthdayType,
  GATriggerType,
  ModalType,
  NodeType,
  PlatformType,
  ProviderActionType,
  ProviderType,
  TriggerType,
} from '../../app/types';
import icFb from '../../assets/images/svg/ic-fb-messenger.svg';
import icInstagram from '../../assets/images/svg/ic-instagram.svg';
import icLine from '../../assets/images/svg/ic-line-official-account.svg';
import icWhatsapp from '../../assets/images/svg/ic_WhatsApp.svg';
import icLivechat from '../../assets/images/svg/ic_livechat.svg';
import {
  clearAutoTrackProductMessage,
  clearImagemapAndImageMessage,
  clearMP34FileMessage,
  clearPhoneBtnMessage,
  clearWaListMessage,
  clearWhatsappTemplateMessage,
  deleteConditionsNode,
  setData,
  setNodeById,
  setSelectedNode,
} from '../../features/mainSlice';
import { closeModal, openModal } from '../../features/modalSlice';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 33,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 3,
    },
    block: {
      display: 'flex',
      alignItems: 'center',
    },
    option: {
      marginRight: 36,
    },
    radioInput: {
      fontSize: 15,
    },
    radio: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      '& img': {
        marginRight: 4,
        width: 20,
        height: 20,
      },
    },
  },
  {
    name: 'Platform',
  },
);

const Platform = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const nodes = useSelector(state => state.ma?.data?.nodes);
  const { platform, limits } = useSelector(state => state.ma?.data);
  const { isActive } = useSelector(store => store.ma);
  const allMessageNode = nodes.filter(n => n.type === NodeType.MESSAGE);
  const allGACondition = nodes.filter(n => n.type === NodeType.GACONDITION);
  const allCondition = nodes.filter(n => n.type === NodeType.CONDITION);
  const triggerNode = nodes.find(n => n.type === NodeType.TRIGGER);

  // 列表型卡片
  const messagesHasWaList = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg =>
          msg.templateType === 'waList' || msg.data.templateType === 'waList',
      ),
  );

  // 訊息範本
  const messagesHasWhatsappTemplate = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg => msg.waTemplateId !== undefined,
      ),
  );

  // 自動帶入商品資訊
  const messagesHasAutoTrackProduct = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(msg => msg.data.autoTrackProduct),
  );

  // 圖文訊息
  const messagesHasImagemap = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg =>
          msg.templateType === 'imagemap' ||
          msg.data.templateType === 'imagemap',
      ),
  );

  // 多頁大圖
  const messagesHasImage = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg =>
          msg.templateType === 'image' || msg.data.templateType === 'image',
      ),
  );
  const messagesHasMp34File = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg =>
          msg.contentType === 'application/x-audio' ||
          msg.contentType === 'application/x-video',
      ),
  );
  const messagesHasPhonebtn = allMessageNode.some(
    n =>
      !!~(n?.data?.messages || []).findIndex(
        msg =>
          msg.contentType === 'application/x-template' &&
          msg?.data?.elements?.some(e =>
            e?.buttons?.some(b => b?.type === 'phone'),
          ),
      ),
  );
  const conditionHasEmoji = allCondition.some(
    n =>
      n?.data?.type === 'right-sticker' ||
      n?.data?.type === 'wrong-sticker' ||
      n?.data?.type === 'n-sticker',
  );

  const triggerHasRejoin = triggerNode?.data?.type === TriggerType.UNBLOCK;
  const triggerHasGA =
    triggerNode?.data?.type === GATriggerType.GAADDTOCART ||
    triggerNode?.data?.type === GATriggerType.GAVIEW ||
    triggerNode?.data?.type === GATriggerType.GAPURCHASE ||
    triggerNode?.data?.type === GATriggerType.GAREMOVEFROMCART;

  // 自定義日期欄位與生日一樣只依顧客資料觸發，切換到 LiveChat 時保留
  const triggerHasBirthday =
    triggerNode?.data?.type === TriggerType.BIRTHDAY ||
    triggerNode?.data?.type === TriggerType.CUSTOM_FIELD_DATE;
  const triggerHasECProvider = Object.values(ProviderType).includes(
    triggerNode?.data?.type,
  );

  const triggerHasBindEc =
    triggerHasECProvider &&
    triggerNode?.data?.action === ProviderActionType.BINDEC;

  const clearGA = value => {
    if (value !== PlatformType.LIVECHAT && value !== PlatformType.LINE) {
      if (triggerHasGA) {
        dispatch(
          setNodeById({
            ...triggerNode,
            data: { type: TriggerType.ADD },
          }),
        );
      }

      if (
        allGACondition?.length &&
        allGACondition?.[0]?.data?.groupId &&
        value !== PlatformType.LIVECHAT &&
        value !== PlatformType.LINE
      ) {
        dispatch(deleteConditionsNode(allGACondition?.[0]?.data?.groupId));
      }
    }
  };

  const clearMessageData = value => {
    if (
      messagesHasAutoTrackProduct &&
      value !== PlatformType.LINE &&
      value !== PlatformType.LIVECHAT
    ) {
      dispatch(clearAutoTrackProductMessage());
    }
    if (messagesHasWhatsappTemplate && value !== PlatformType.WHATSAPP) {
      dispatch(clearWhatsappTemplateMessage());
    }
    if (messagesHasWaList && value !== PlatformType.WHATSAPP) {
      dispatch(clearWaListMessage());
    }
    if (
      (messagesHasImagemap || messagesHasImage) &&
      value !== PlatformType.LINE
    ) {
      dispatch(clearImagemapAndImageMessage());
    }
    if (messagesHasMp34File && value === PlatformType.INSTAGRAM) {
      dispatch(clearMP34FileMessage());
    }
    if (messagesHasPhonebtn && value === PlatformType.INSTAGRAM) {
      dispatch(clearPhoneBtnMessage());
    }
  };

  const clearTriggerData = value => {
    if (
      value === PlatformType.LIVECHAT &&
      ((!triggerHasGA && !triggerHasECProvider && !triggerHasBirthday) ||
        triggerHasBindEc)
    ) {
      dispatch(
        setNodeById({
          ...triggerNode,
          data: { type: TriggerType.BIRTHDAY, conditions: BirthdayType.DAY },
        }),
      );
    } else if (triggerHasRejoin || triggerHasECProvider) {
      dispatch(
        setNodeById({
          ...triggerNode,
          data: { type: TriggerType.ADD },
        }),
      );
    }
    dispatch(setSelectedNode(null));
  };

  const clearSetting = value => {
    if (value) {
      dispatch(
        setData({
          platform: value,
          limits: {
            ...limits,
            message: undefined,
          },
        }),
      );
    } else {
      dispatch(
        setData({
          platform,
        }),
      );
    }
  };

  const clearEmoij = value => {
    if (value !== PlatformType.LINE && conditionHasEmoji) {
      const firstEmojiCondition = allCondition?.filter(
        c =>
          c?.data?.type === 'right-sticker' ||
          c?.data?.type === 'wrong-sticker' ||
          c?.data?.type === 'n-sticker',
      );

      if (firstEmojiCondition && firstEmojiCondition?.length) {
        dispatch(deleteConditionsNode(firstEmojiCondition?.[0]?.data?.groupId));
      }
    }
  };

  const onChange = value => {
    // ec事件只支援line、livechat
    const hasEC =
      triggerHasECProvider &&
      value !== PlatformType.LIVECHAT &&
      value !== PlatformType.LINE;
    // ga事件只支援line、livechat
    const hasGaEvent =
      (triggerHasGA || (allGACondition?.length && allGACondition?.[0]?.id)) &&
      value !== PlatformType.LIVECHAT &&
      value !== PlatformType.LINE;
    // ig 發送訊息不支援電話按鈕、影片
    const IgMessageCheck =
      (value === PlatformType.INSTAGRAM && messagesHasMp34File) ||
      (value === PlatformType.INSTAGRAM && messagesHasPhonebtn);
    // livechat 觸發條件只有 ga、ec(不支援會員綁定完當下)、生日
    const livechatTriggerNodeCheck =
      value === PlatformType.LIVECHAT &&
      ((!triggerHasGA && !triggerHasECProvider && !triggerHasBirthday) ||
        triggerHasBindEc);
    // 進入條件-顧客發送貼圖 只支援line
    const emojiConditionCheck =
      value !== PlatformType.LINE && conditionHasEmoji;
    // 除了line其他平台不支援多頁大圖與圖文訊息
    // 除了whatsapp其他平台不支援訊息範本與列表型卡片
    const messageError =
      ((messagesHasImagemap || messagesHasImage) &&
        value !== PlatformType.LINE) ||
      ((messagesHasWhatsappTemplate || messagesHasWaList) &&
        value !== PlatformType.WHATSAPP);
    // 除了line livechat其他平台不支援自動帶入商品資訊
    const hasAutoTrackProduct =
      messagesHasAutoTrackProduct &&
      value !== PlatformType.LINE &&
      value !== PlatformType.LIVECHAT;
    // 解除封鎖只支援line
    const hasRejoin = triggerHasRejoin && value !== PlatformType.LINE;

    if (
      messageError ||
      hasAutoTrackProduct ||
      hasRejoin ||
      IgMessageCheck ||
      hasGaEvent ||
      hasEC ||
      livechatTriggerNodeCheck ||
      emojiConditionCheck
    ) {
      dispatch(
        openModal({
          modalType: ModalType.DIALOG_TEXT,
          modalProps: {
            title: t(`platform.change-modal-title`),
            content: (
              <>
                {t(`platform.change-modal-content1`)}
                <br />
                {t(`platform.change-modal-content2`)}
              </>
            ),
            cancelText: t(`platform.change-modal-canceltext`),
            submitText: t(`platform.change-modal-submitText`),
          },
          onClick: () => {
            clearEmoij(value);
            clearSetting(value);
            clearGA(value);
            clearMessageData(value);
            clearTriggerData(value);
            dispatch(closeModal());
          },
          onCancel: () => {
            clearSetting();
          },
        }),
      );
    }

    clearSetting(value);
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t(`platform.title`)}</div>
      <FormControl component="fieldset">
        <RadioGroup
          row
          aria-label="default-display"
          name="default-display"
          value={platform}
          onChange={e => onChange(e?.target?.value)}
        >
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={PlatformType.LINE}
            control={<Radio color="primary" disabled={isActive} />}
            label={
              <div className={classes.radio}>
                <img src={icLine} alt="import" />
                {t(`platform.line`)}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={PlatformType.FACEBOOK}
            control={<Radio color="primary" disabled={isActive} />}
            label={
              <div className={classes.radio}>
                <img src={icFb} alt="import" />
                {t(`platform.facebook`)}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={PlatformType.INSTAGRAM}
            control={<Radio color="primary" disabled={isActive} />}
            label={
              <div className={classes.radio}>
                <img src={icInstagram} alt="import" />
                {t(`platform.instagram`)}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={PlatformType.WHATSAPP}
            control={<Radio color="primary" disabled={isActive} />}
            label={
              <div className={classes.radio}>
                <img src={icWhatsapp} alt="import" />
                {t(`platform.whatsapp`)}
              </div>
            }
          />
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={PlatformType.LIVECHAT}
            control={<Radio color="primary" disabled={isActive} />}
            label={
              <div className={classes.radio}>
                <img src={icLivechat} alt="import" />
                {t(`platform.livechat`)}
              </div>
            }
          />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

export default Platform;
